import {Box, Button, Stack, Typography, useTheme} from '@mui/material';
import React, {Fragment} from 'react';

import {PreviewRefundType} from 'domain/usecases';
import {QUERY_KEYS} from 'infra/libs/react-query/queryKeys';
import {FinishWithoutCompleteFlowEnum} from 'main/analytics/gaTagsEnum';
import {EmptySearchState, SentinelLoading} from 'presentation/components';
import {useGetPaginatedItemsReactQuery} from 'presentation/pages/private/hooks/useGetPaginatedItemsReactQuery';
import {FailureAlert, PreviewCardNew} from 'presentation/pages/private/refund/components';
import {useConsultRefund} from 'presentation/pages/private/refund/hooks';
import {translations} from 'presentation/translations-new';
import {getConsultPreviewRefund} from '../../../ConsultRefundPreview/service/getConsultPreviewRefund';
import {StyledGrid} from '../ListStep/styles';

const {goBack} = translations['pt-br'].commons;
const {emptyPreviewRefundState, emptyRefundDescription, foundResults} =
	translations['pt-br'].privateFlow.consultPreviewRefund;

export function PreviewRefundCardList() {
	const {palette} = useTheme();
	const {consultEndDate, consultInitialDate, handleResetQuery} = useConsultRefund();

	const {
		items,
		loadingItemsList,
		itemsListIsEmpty,
		itemsListHasError,
		totalItems,
		loadMore,
		refresh,
	} = useGetPaginatedItemsReactQuery<PreviewRefundType>({
		queryKey: [QUERY_KEYS.REFUND_CONSULT_PREVIEW, consultInitialDate, consultEndDate],
		queryFn: ({page}) =>
			getConsultPreviewRefund({
				startDate: consultInitialDate,
				endDate: consultEndDate,
				page,
				limit: 12,
			}),
		queryOptions: {
			enabled: !!consultInitialDate && !!consultEndDate,
			refetchOnWindowFocus: false,
		},
	});

	if (itemsListHasError) {
		return (
			<StyledGrid flex={1}>
				<Stack gridColumn="1/-1" gridRow="1/-1" justifyContent="center" alignSelf="center">
					<FailureAlert refetch={refresh} />
				</Stack>
			</StyledGrid>
		);
	}

	if (itemsListIsEmpty) {
		return (
			<Box gridColumn="1/-1">
				<EmptySearchState
					title={emptyPreviewRefundState}
					description={emptyRefundDescription}
					dataTagId={FinishWithoutCompleteFlowEnum.SEM_REEMBOLSO_ENCONTRADO}>
					<Button variant="outlined" size="large" onClick={handleResetQuery}>
						{goBack}
					</Button>
				</EmptySearchState>
			</Box>
		);
	}

	const totalResults = totalItems ? totalItems.toString().padStart(2, '0') : 0;

	return (
		<Fragment>
			{!loadingItemsList && items ? (
				<Typography color={palette.customInfo.shades?.['160p']}>
					<b>{totalResults}</b> {foundResults}
				</Typography>
			) : null}

			<StyledGrid>
				{items.map((item, index) => (
					<PreviewCardNew key={index} data={item} />
				))}
			</StyledGrid>
			<SentinelLoading
				minHeight="200px"
				onVisible={() => {
					if (items.length > 0) {
						loadMore();
					}
				}}
				isLoading={loadingItemsList}
			/>
		</Fragment>
	);
}
